/**
 * Ad Click ID Tracking
 *
 * Captures gclid (Google Ads), fbclid (Meta), and msclkid (Microsoft Ads)
 * from URL parameters and persists them in first-party cookies for 90 days
 * (matches Google Ads' default click-through conversion window).
 *
 * Last-click wins: a new click ID in the URL overwrites the cookie, but
 * pages without an ad param leave the existing cookie untouched.
 */

const AD_CLICK_PARAMS = ['gclid', 'fbclid', 'msclkid'] as const;
export type AdClickParam = typeof AD_CLICK_PARAMS[number];

const NINETY_DAYS_SECONDS = 60 * 60 * 24 * 90;

function setCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${NINETY_DAYS_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (isHttps) parts.push('Secure');
  document.cookie = parts.join('; ');
}

/**
 * Read an ad click ID from the cookie jar.
 * Returns null if missing or unset.
 */
export function getAdClickId(name: AdClickParam): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  const raw = match.split('=').slice(1).join('=');
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    return raw || null;
  }
}

/**
 * Inspect the current URL and persist any ad click IDs to cookies.
 * No-op when no ad params are present (preserves existing cookie).
 * Safe to call on every route change.
 */
export function captureAdClickIds(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of AD_CLICK_PARAMS) {
      const value = params.get(key);
      if (value && value.trim()) {
        setCookie(key, value.trim());
      }
    }
  } catch {
    // ignore — never break the page over tracking
  }
}
